/**
 * The folders of «Мои проекты», as the app files its projects: a project's folder is its room type (the project's
 * `RoomType`, `scene.roomType` here), which the app's «Переместить в папку» sets (MoveRoomToFolder: the room types of
 * its room builder, `RoomTypesList` with the floor plan and without the empty type) and which its «Мои проекты» lists
 * one shortcut per type (RoomTypeShortcutsList), «Другие комнаты» for a project with no type. The titles are the app's
 * translation keys: `title` the type's own (#RoomKitchen «Кухня»), `folder` the shortcut's (#RoomKitchenManyMy «Мои кухни»).
 */
import { plural, t, type TranslationKey } from '../i18n/I18n';

export interface ProjectFolder { id: string; readonly title: string; readonly folder: string }
const entry = (id: string, title: TranslationKey, folder: TranslationKey): ProjectFolder =>
  ({ id, get title() { return t(title); }, get folder() { return t(folder); } });

/** The folders a project can be moved to, in the app's order: «План помещения» first, the room types after it. */
export const PROJECT_FOLDERS: readonly ProjectFolder[] = [
  entry('all', '#RoomFloorplan', '#RoomFloorplanManyMy'),
  entry('bedroom', '#RoomBedroom', '#RoomBedroomManyMy'),
  entry('livingroom', '#RoomLiving', '#RoomLivingManyMy'),
  entry('kitchens', '#RoomKitchen', '#RoomKitchenManyMy'),
  entry('outdoor', '#RoomOutdoor', '#RoomOutdoorManyMy'),
  entry('kids', '#RoomBabyKids', '#RoomBabyKidsManyMy'),
  entry('bathroom', '#RoomBath', '#RoomBathManyMy'),
  entry('closet', '#RoomCloset', '#RoomClosetManyMy'),
  entry('store', '#RoomStore', '#RoomStoreManyMy'),
  entry('homeoffice', '#RoomHomeOffice', '#RoomHomeOfficeManyMy'),
  entry('cinema', '#RoomHomeCinema', '#RoomHomeCinemaManyMy'),
  entry('cafe', '#RoomCafe', '#RoomCafeManyMy'),
  entry('garage', '#RoomGarage', '#RoomGarageManyMy'),
  entry('hall', '#RoomHall', '#RoomHallManyMy'),
  entry('dining', '#RoomDining', '#RoomDiningManyMy'),
  entry('gym', '#RoomGym', '#RoomGymManyMy'),
  entry('laundry', '#RoomLaundry', '#RoomLaundryManyMy'),
  entry('reception', '#RoomReception', '#RoomReceptionManyMy'),
  entry('coworking', '#RoomCoworking', '#RoomCoworkingManyMy'),
  entry('salon', '#RoomSalon', '#RoomSalonManyMy'),
  entry('event', '#RoomEvent', '#RoomEventManyMy'),
];

/** A project without a type, or with one the app does not list, is in the app's «Другие комнаты». */
export const OTHER_ROOMS = '';
/** The trash's place among the folders; never a room type, so never a project's own folder. */
export const RECENTLY_DELETED = 'deleted';

/** Names older projects carry for the app's types (the app reads «open» as the floor plan, GetRoomTypeInfo). */
const ALIASES: Record<string, string> = {
  open: 'all', kitchen: 'kitchens', living: 'livingroom', diningroom: 'dining', office: 'homeoffice', nursery: 'kids', wardrobe: 'closet',
};

/** The folder a project's stored room type files it in. */
export function folderOf(roomType: string | undefined): string {
  const key = (roomType ?? '').trim().toLowerCase();
  const id = ALIASES[key] ?? key;
  return PROJECT_FOLDERS.some(folder => folder.id === id) ? id : OTHER_ROOMS;
}

/** The folder's name in the list, the app's «Мои кухни»; «Другие комнаты» for the rest and «Недавно удалённые» for the trash. */
export function folderTitle(id: string): string {
  if (id === RECENTLY_DELETED) return t('#FolderRecentlyDeleted');
  return PROJECT_FOLDERS.find(folder => folder.id === id)?.folder ?? t('Other Rooms');
}

/** What a project in «Недавно удалённые» has left there, the app's GetLeftDays in a sentence: «Осталось 14 дней». */
export function daysLeftLabel(days: number): string {
  if (days <= 0) return t('#FolderLessThanDayLeft');
  return plural('#FolderDaysLeft', days);
}

const REMEMBERED = 'hp.projects.folder';
/**
 * The folder «Мои проекты» was left on, for this tab: a project opened from a folder closes back to it, as the app's
 * back does. Null is «Все проекты».
 */
export function rememberedFolder(): string | null {
  try { return sessionStorage.getItem(REMEMBERED); } catch { return null; }
}
export function rememberFolder(id: string | null): void {
  try { if (id === null) sessionStorage.removeItem(REMEMBERED); else sessionStorage.setItem(REMEMBERED, id); } catch { /* Then the list opens on every project. */ }
}
